import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, useRouterState, useNavigate } from "@tanstack/react-router";
import { Calendar, LayoutGrid, Sparkles, Users, Plus, Columns, LayoutDashboard, ChevronDown, Check, X, ListTodo, Download, Upload, Tag, GitBranch, FileText, Package, Edit2, Menu, Search } from "lucide-react";
import { useMainNavResize } from "./useMainNavResize";
import { useIsMobile } from "../hooks/useIsMobile";
import { QtplanLogo } from "./QtplanLogo";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import type { NavStage } from "../stores/mainNavStore";

// Tooltip component that appears to the right using portal
function Tooltip({ children, label, className }: { children: ReactNode; label: string; className?: string }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
    setVisible(true);
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {visible && createPortal(
        <div
          className="fixed px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-[9999] pointer-events-none shadow-lg"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateY(-50%)',
          }}
        >
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
        </div>,
        document.body
      )}
    </div>
  );
}
import { useSkillsActions, useEmployeesActions, useTeamsActions, useActiveBoardIdFromStore, useBoards, useUIActions, getTasksArray, getTaskIdCounter, initBoardTaskViewStore, initBoardColumnsStore, recalculateSchedule } from "../stores";
import { skillsStore } from "../stores/skillsStore";
import { employeesStore } from "../employees/employeesStore";
import { teamsStore } from "../teams/teamsStore";
import { globalRequirementsStore } from "../stores/globalRequirementsStore";
import { globalTasksStore } from "../stores/globalTasksStore";
import { boardsStore } from "../stores/boardsStore";
import type { BoardDefinition, GlobalTask } from "../types";

// Format ISO timestamp as DD.MM.YYYY HH:MM
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
}

// Board list popover content (shared between expanded & collapsed)
function BoardListPopover({
  boards,
  activeBoard,
  onSelect,
  onAdd,
}: {
  boards: BoardDefinition[];
  activeBoard: BoardDefinition | undefined;
  onSelect: (boardId: string) => void;
  onAdd: () => void;
}) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [search, setSearch] = useState('');

  const handleStartRename = (board: BoardDefinition, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(board.id);
    setEditName(board.name);
  };

  const handleSaveRename = (boardId: string) => {
    if (!editName.trim()) { setEditingId(null); return; }
    boardsStore.send({ type: 'board.update', boardId, updates: { name: editName.trim() } });
    setEditingId(null);
  };

  const handleCancelRename = () => {
    setEditingId(null);
  };

  // Filter by search, then sort: active board first, rest by updatedAt desc
  const sortedBoards = useMemo(() => {
    const filtered = search
      ? boards.filter(b => b.name.toLowerCase().includes(search.toLowerCase()))
      : boards;
    return [...filtered].sort((a, b) => {
      if (a.id === activeBoard?.id) return -1;
      if (b.id === activeBoard?.id) return 1;
      return (b.updatedAt || '').localeCompare(a.updatedAt || '');
    });
  }, [boards, activeBoard, search]);

  return (
    <>
      {/* Search field – only when >2 boards */}
      {boards.length > 2 && (
        <div className="px-2 pt-1.5 pb-1">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('nav.searchBoards')}
              className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:bg-white"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* No results */}
      {sortedBoards.length === 0 && search && (
        <div className="px-3 py-2 text-sm text-gray-400">{t('nav.noResults')}</div>
      )}

      {/* Board entries */}
      {sortedBoards.map((board) => (
        <div key={board.id}>
          {editingId === board.id ? (
            <div className="flex items-center gap-1 px-3 py-1" onClick={e => e.stopPropagation()}>
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="flex-1 min-w-0 px-1.5 py-0.5 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                autoFocus
                onFocus={e => e.target.select()}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveRename(board.id);
                  if (e.key === 'Escape') handleCancelRename();
                }}
              />
              <button onClick={() => handleSaveRename(board.id)} className="p-0.5 text-green-600 hover:bg-green-50 rounded">
                <Check size={12} />
              </button>
              <button onClick={handleCancelRename} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className={`w-full flex items-start gap-2 px-3 py-1.5 text-sm text-left transition-colors group ${
              board.id === activeBoard?.id
                ? 'border-l-2 border-indigo-500 bg-indigo-50/50'
                : 'border-l-2 border-transparent hover:bg-gray-50'
            }`}>
              <button
                onClick={() => onSelect(board.id)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="font-medium text-gray-900 truncate">{board.name}</div>
                {board.updatedAt && (
                  <div className="text-[11px] text-gray-400 mt-0.5">{formatDateTime(board.updatedAt)}</div>
                )}
              </button>
              <button
                onClick={(e) => handleStartRename(board, e)}
                className="flex-shrink-0 p-0.5 rounded text-gray-300 opacity-0 group-hover:opacity-100 hover:text-gray-600 hover:bg-gray-100 transition-all mt-0.5"
                title={t('common.rename')}
              >
                <Edit2 size={11} />
              </button>
            </div>
          )}
        </div>
      ))}
      {boards.length > 0 && <div className="border-t border-gray-100 my-1" />}
      <button
        onClick={onAdd}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <Plus size={12} strokeWidth={2} />
        <span>{t('nav.newBoard')}</span>
      </button>
    </>
  );
}

// Custom board switcher – expanded mode (dropdown below trigger)
function BoardSwitcher({
  boards,
  activeBoard,
  onSelect,
  onAdd,
}: {
  boards: BoardDefinition[];
  activeBoard: BoardDefinition | undefined;
  onSelect: (boardId: string) => void;
  onAdd: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [open, close]);

  return (
    <div ref={containerRef} className="relative px-1.5 pb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-left transition-colors hover:bg-gray-50 group"
      >
        <span className="flex-1 truncate font-medium text-gray-700 group-hover:text-gray-900">
          {activeBoard?.name || t('nav.selectBoard')}
        </span>
        <ChevronDown
          size={12}
          className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-1.5 right-1.5 top-full z-50 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg py-1 min-w-72 max-h-64 overflow-y-auto">
          <BoardListPopover
            boards={boards}
            activeBoard={activeBoard}
            onSelect={(id) => { onSelect(id); close(); }}
            onAdd={() => { onAdd(); close(); }}
          />
        </div>
      )}
    </div>
  );
}

// Collapsed board switcher – portal popover to the right of the sidebar
function CollapsedBoardSwitcher({
  boards,
  activeBoard,
  isBoardRoute,
  onSelect,
  onAdd,
  iconSize,
}: {
  boards: BoardDefinition[];
  activeBoard: BoardDefinition | undefined;
  isBoardRoute: boolean;
  onSelect: (boardId: string) => void;
  onAdd: () => void;
  iconSize: number;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        popoverRef.current && !popoverRef.current.contains(e.target as Node)
      ) close();
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [open, close]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.top, left: rect.right + 8 });
    }
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`flex items-center justify-center w-full p-1.5 rounded-md transition-colors mb-0.5 ${
          isBoardRoute || open
            ? "bg-gray-100 text-gray-900"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
        }`}
      >
        <LayoutDashboard size={iconSize} strokeWidth={1.5} className={`flex-shrink-0 ${isBoardRoute || open ? "text-gray-700" : "text-gray-400"}`} />
      </button>
      {open && createPortal(
        <div
          ref={popoverRef}
          className="fixed bg-white rounded-lg border border-gray-200 shadow-lg py-1 min-w-72 max-h-64 overflow-y-auto z-[9999]"
          style={{ top: position.top, left: position.left }}
        >
          <BoardListPopover
            boards={boards}
            activeBoard={activeBoard}
            onSelect={(id) => { onSelect(id); close(); }}
            onAdd={() => { onAdd(); close(); }}
          />
        </div>,
        document.body
      )}
    </>
  );
}
import { AddSkillModal } from "./skills";
import { AddEmployeeModal } from "../employees/components";
import { AddTeamModal } from "../teams/components";
import { TaskModal } from "./tasks";
import type { TaskModalMode } from "./tasks";
import { AddColumnModal } from "./columns/AddColumnModal";
import { AdrModal } from "../adr/components/AdrModal";
import { ResourceModal } from "../resources/components/ResourceModal";

function getStageFlags(navStage: NavStage) {
  const showLabels = navStage === 'expanded';
  const showSmallLabels = navStage === 'icons';
  const showAnyLabels = showLabels || showSmallLabels;
  const showPlusButtons = navStage === 'icons' || navStage === 'expanded';
  const showSectionHeaders = navStage === 'expanded';
  const showImportExportText = navStage === 'expanded';
  const iconSize = navStage === 'tiny' ? 12 : navStage === 'small' ? 14 : 16;
  return { showLabels, showSmallLabels, showAnyLabels, showPlusButtons, showSectionHeaders, showImportExportText, iconSize };
}

export function RootLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const activeBoardId = useActiveBoardIdFromStore();
  const boards = useBoards();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Mobile detection
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Auto-close mobile nav on route change
  useEffect(() => {
    if (isMobile) setMobileNavOpen(false);
  }, [currentPath, isMobile]);

  // Multi-stage nav resize
  const { navWidth, navStage, isResizing, handleResizeStart, handleDoubleClick } = useMainNavResize();
  const mobileStageFlags = getStageFlags('expanded');
  const desktopStageFlags = getStageFlags(navStage);
  const { showLabels, showSmallLabels, showAnyLabels, showPlusButtons, showSectionHeaders, showImportExportText, iconSize } = isMobile ? mobileStageFlags : desktopStageFlags;

  // Modal states
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showAddAdrModal, setShowAddAdrModal] = useState(false);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);

  // TaskModal state: unified create + edit
  const [taskModalState, setTaskModalState] = useState<{
    isOpen: boolean;
    mode: TaskModalMode;
    taskHistory: string[];
    historyIndex: number;
    taskId?: string;
  } | null>(null);

  const openTaskModalCreate = useCallback(() => {
    setTaskModalState({ isOpen: true, mode: 'create', taskHistory: [], historyIndex: -1 });
  }, []);

  const closeTaskModal = useCallback(() => {
    setTaskModalState(null);
  }, []);

  const handleTaskModalNavigate = useCallback((navTaskId: string, newIndex: number) => {
    setTaskModalState(prev => {
      if (!prev) return prev;
      if (navTaskId === '__create__') {
        return { ...prev, mode: 'create' as TaskModalMode, taskId: undefined, historyIndex: newIndex };
      }
      return { ...prev, mode: 'edit' as TaskModalMode, taskId: navTaskId, historyIndex: newIndex };
    });
  }, []);

  // Global keyboard shortcut: C to create task
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable) return;
        if (!activeBoardId) return;
        e.preventDefault();
        openTaskModalCreate();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeBoardId, openTaskModalCreate]);

  // Actions (global only)
  const { addNewSkill } = useSkillsActions();
  const { addNewEmployee } = useEmployeesActions();
  const { addNewTeam } = useTeamsActions();
  const { openImportModal } = useUIActions();

  const handleExport = () => {
    if (!activeBoardId) return;
    const tasks = getTasksArray(activeBoardId);
    const skills = skillsStore.getSnapshot().context.skills;
    const emps = employeesStore.getSnapshot().context.employees;
    const teams = teamsStore.getSnapshot().context.teams;
    const columns = initBoardColumnsStore(activeBoardId).getSnapshot().context.columns;
    const globalRequirements = globalRequirementsStore.getSnapshot().context.requirements;

    const savedView = {
      version: 1,
      tasks: [],
      allTasks: tasks.map((t) => ({
        id: t.id, title: t.title, column: t.column, startDate: t.startDate,
        estimateMode: t.estimateMode || 'default',
        ...(t.defaultEstimate && { defaultEstimate: t.defaultEstimate }),
        estimates: t.estimates, assigneeIds: t.assigneeIds, categoryIds: t.categoryIds ?? [],
        ...(typeof t.description === 'string' && { description: t.description }),
        ...(typeof t.consequence === 'string' && { consequence: t.consequence }),
        ...(typeof t.requiredSkills === 'string' && { requiredSkills: t.requiredSkills }),
        ...(t.requirements_hard && { requirements_hard: t.requirements_hard }),
        ...(t.requirements_soft && { requirements_soft: t.requirements_soft }),
      })),
      skills: skills.map((s) => ({ id: s.id, emoji: s.emoji, name: s.name, description: s.description })),
      employees: emps.map((e) => ({ id: e.id, name: e.name, color: e.color, team: e.team, skills: e.skills })),
      teams: teams.map((t) => ({ id: t.id, name: t.name, color: t.color })),
      columns,
      globalRequirements,
      taskIdCounter: getTaskIdCounter(),
    };
    navigator.clipboard.writeText(JSON.stringify(savedView, null, 2));
    alert(t('exportAlert'));
  };

  const handleImport = () => {
    if (!activeBoardId) return;
    navigate({ to: '/boards/$boardId/roadmap', params: { boardId: activeBoardId } });
    setTimeout(() => openImportModal(), 100);
  };

  // Board switching
  const activeBoard = boards.find(b => b.id === activeBoardId);

  const handleBoardChange = (boardId: string) => {
    boardsStore.send({ type: 'board.setActive', boardId });
    navigate({ to: '/boards/$boardId/roadmap', params: { boardId } });
  };

  // Landing / audience pages: render only <Outlet /> without sidebar
  const isLandingPage = currentPath === '/'
    || currentPath.startsWith('/de/for/') || currentPath.startsWith('/en/for/')
    || currentPath.startsWith('/de/features/') || currentPath.startsWith('/en/features/')
    || currentPath === '/de/pricing' || currentPath === '/en/pricing'
    || currentPath.startsWith('/de/legal/') || currentPath.startsWith('/en/legal/');
  if (isLandingPage) {
    return <Outlet />;
  }

  // Determine active page based on path
  const isBoardRoute = currentPath.startsWith("/boards/") && currentPath !== "/boards";
  const isRoadmap = isBoardRoute && currentPath.endsWith("/roadmap");
  const isOverview = isBoardRoute && currentPath.endsWith("/overview");
  const isColumns = isBoardRoute && currentPath.endsWith("/columns");
  const isCanvas = isBoardRoute && currentPath.endsWith("/canvas");
  const isEditor = isBoardRoute && currentPath.endsWith("/editor");
  const isBoardsPage = currentPath === "/boards";
  const isSkills = currentPath === "/skills";
  const isTeams = currentPath === "/teams";
  const isEmployees = currentPath === "/employees";
  const isTasks = currentPath === "/tasks";
  const isCategories = currentPath === "/categories";
  const isAdrs = currentPath === "/adrs";
  const isResources = currentPath === "/resources";

  // Board-scoped navigation items
  const boardViewItems = activeBoardId ? [
    { path: '/roadmap', label: t('nav.roadmap'), icon: Calendar, active: isRoadmap, onAdd: () => openTaskModalCreate() },
    { path: '/overview', label: t('nav.overview'), icon: LayoutGrid, active: isOverview, onAdd: undefined as (() => void) | undefined },
    { path: '/columns', label: "Columns", icon: Columns, active: isColumns, onAdd: () => setShowAddColumnModal(true) },
    { path: '/canvas', label: t('nav.canvas'), icon: GitBranch, active: isCanvas, onAdd: undefined as (() => void) | undefined },
    { path: '/editor', label: t('nav.editor'), icon: FileText, active: isEditor, onAdd: undefined as (() => void) | undefined },
  ] : [];

  const adminItems = [
    { to: "/boards" as const, label: t('nav.boards'), icon: LayoutDashboard, active: isBoardsPage, onAdd: () => navigate({ to: '/boards' }) },
    { to: "/tasks" as const, label: t('nav.tasks'), icon: ListTodo, active: isTasks, onAdd: () => openTaskModalCreate() },
    { to: "/adrs" as const, label: t('nav.adrs'), icon: FileText, active: isAdrs, onAdd: () => setShowAddAdrModal(true) },
    { to: "/resources" as const, label: t('nav.resources'), icon: Package, active: isResources, onAdd: () => setShowAddResourceModal(true) },
    { to: "/teams" as const, label: t('nav.teams'), icon: Users, active: isTeams, onAdd: () => setShowAddTeamModal(true) },
    { to: "/employees" as const, label: t('nav.employees'), icon: Users, active: isEmployees, onAdd: () => setShowAddEmployeeModal(true) },
    { to: "/categories" as const, label: t('nav.categories'), icon: Tag, active: isCategories, onAdd: undefined as (() => void) | undefined },
    { to: "/skills" as const, label: t('nav.skills'), icon: Sparkles, active: isSkills, onAdd: () => setShowAddSkillModal(true) },
  ];

  // Nav item classes based on stage
  const navItemClasses = (active: boolean) => {
    let sizeClasses: string;
    if (showLabels) {
      sizeClasses = 'gap-2.5 px-2.5 py-1.5 text-[10px]';
    } else if (showSmallLabels) {
      sizeClasses = 'gap-1.5 px-1.5 py-1 text-[10px]';
    } else {
      sizeClasses = 'p-1.5';
    }
    return `flex items-center rounded-md transition-colors w-full ${sizeClasses} ${
      active
        ? "bg-gray-100 text-gray-900 font-medium"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
    }`;
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Mobile Hamburger Button */}
      {isMobile && !mobileNavOpen && (
        <button
          onClick={() => setMobileNavOpen(true)}
          style={{ position: 'fixed', top: 12, left: 12, zIndex: 40 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-2"
          aria-label="Open navigation"
        >
          <Menu size={24} className="text-gray-700" />
        </button>
      )}

      {/* Mobile Backdrop */}
      {isMobile && mobileNavOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.3)' }}
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      {(!isMobile || mobileNavOpen) && (
      <nav
        className="bg-white border-r border-gray-200 flex flex-col print:hidden overflow-hidden"
        style={isMobile
          ? { position: 'fixed', top: 0, left: 0, bottom: 0, width: 260, zIndex: 50, boxShadow: '0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1)' }
          : {
              position: 'sticky',
              top: 0,
              height: '100vh',
              width: navWidth,
              minWidth: navWidth,
              transition: isResizing ? 'none' : 'width 200ms ease, min-width 200ms ease',
            }
        }
      >
        {/* Logo/Title */}
        <div className={`flex items-center border-b border-gray-100 ${showLabels ? 'px-4 py-4' : 'px-1 py-2 justify-center'}`}>
          {showLabels ? (
            <div className="flex items-center gap-1 text-gray-900 font-medium text-base truncate">
              qtplan
              <QtplanLogo size={16} />
            </div>
          ) : (
            <QtplanLogo size={16} className="text-gray-900" />
          )}
        </div>

        {/* BOARD section - only show when boards exist */}
        {boards.length > 0 && (
          <div className={`py-3 ${showLabels ? 'px-2' : 'px-1'}`}>
            {showSectionHeaders && (
              <div className="px-2.5 pb-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                Board
              </div>
            )}

            {/* Board Switcher */}
            {showLabels ? (
              <BoardSwitcher
                boards={boards}
                activeBoard={activeBoard}
                onSelect={handleBoardChange}
                onAdd={() => navigate({ to: '/boards' })}
              />
            ) : (
              <CollapsedBoardSwitcher
                boards={boards}
                activeBoard={activeBoard}
                isBoardRoute={isBoardRoute}
                onSelect={handleBoardChange}
                onAdd={() => navigate({ to: '/boards' })}
                iconSize={iconSize}
              />
            )}

            {/* Board view links (only when a board is selected) */}
            {activeBoardId && (
              <div className="flex flex-col gap-0.5">
                {boardViewItems.map((item) => (
                  <div key={item.path} className="flex items-center group">
                    <Tooltip label={item.label} className="flex-1 min-w-0">
                      <Link
                        to={"/boards/$boardId" + item.path as "/boards/$boardId/roadmap"}
                        params={{ boardId: activeBoardId }}
                        className={navItemClasses(item.active)}
                      >
                        <item.icon size={iconSize} strokeWidth={1.5} className={`flex-shrink-0 ${item.active ? "text-gray-700" : "text-gray-400"}`} />
                        {showAnyLabels && <span className="truncate">{item.label}</span>}
                      </Link>
                    </Tooltip>
                    {showPlusButtons && item.onAdd && (
                      <Tooltip label={t('nav.addTooltip', { label: item.label })}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            item.onAdd?.();
                          }}
                          className="flex-shrink-0 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors p-1"
                        >
                          <Plus size={12} strokeWidth={2} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Separator */}
        <div className={`border-t border-gray-100 ${showLabels ? 'mx-3' : 'mx-1'}`} />

        {/* VERWALTUNG */}
        <div className={`py-3 ${showLabels ? 'px-2' : 'px-1'}`}>
          {showSectionHeaders && (
            <div className="px-2.5 pb-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              {t('nav.globalSection')}
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            {adminItems.map((item) => (
              <div key={item.to} className="flex items-center group">
                <Tooltip label={item.label} className="flex-1 min-w-0">
                  <Link
                    to={item.to}
                    className={navItemClasses(item.active)}
                  >
                    <item.icon size={iconSize} strokeWidth={1.5} className={`flex-shrink-0 ${item.active ? "text-gray-700" : "text-gray-400"}`} />
                    {showAnyLabels && <span className="truncate">{item.label}</span>}
                  </Link>
                </Tooltip>
                {showPlusButtons && item.onAdd && (
                  <Tooltip label={t('nav.addTooltip', { label: item.label })}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        item.onAdd?.();
                      }}
                      className="flex-shrink-0 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors p-1"
                    >
                      <Plus size={12} strokeWidth={2} />
                    </button>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom spacer */}
        <div className="flex-1" />

        {/* Language Switcher */}
        <div className={`${showLabels ? 'px-3 pb-1' : 'px-1 pb-1 flex justify-center'}`}>
          <LanguageSwitcher compact={!showLabels} />
        </div>

        {/* Import/Export - always visible when a board exists */}
        {activeBoardId && (
          <div className={`py-2 border-t border-gray-100 ${showLabels ? 'px-3' : 'px-1'}`}>
            <div className={showLabels ? "flex gap-1" : "flex flex-col gap-0.5 items-center"}>
              <Tooltip label={t('nav.exportTooltip')}>
                <button
                  onClick={handleExport}
                  className={`flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors ${
                    showLabels ? 'flex-1 gap-1.5 px-2 py-1.5 text-xs' : 'w-full p-1.5'
                  }`}
                >
                  <Download size={14} strokeWidth={1.5} />
                  {showImportExportText && "Export"}
                </button>
              </Tooltip>
              <Tooltip label={t('nav.importTooltip')}>
                <button
                  onClick={handleImport}
                  className={`flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors ${
                    showLabels ? 'flex-1 gap-1.5 px-2 py-1.5 text-xs' : 'w-full p-1.5'
                  }`}
                >
                  <Upload size={14} strokeWidth={1.5} />
                  {showImportExportText && "Import"}
                </button>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Resize Handle (hidden on mobile) */}
        {!isMobile && (
          <div
            onMouseDown={handleResizeStart}
            onDoubleClick={handleDoubleClick}
            style={{
              position: 'absolute',
              top: 0,
              right: -2,
              width: 4,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 50,
              backgroundColor: isResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!isResizing) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
            }}
            onMouseLeave={(e) => {
              if (!isResizing) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          />
        )}
      </nav>
      )}

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* Global Modals */}
      <AddSkillModal
        isOpen={showAddSkillModal}
        onClose={() => setShowAddSkillModal(false)}
        onAdd={addNewSkill}
      />
      <AddEmployeeModal
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onAdd={addNewEmployee}
      />
      <AddTeamModal
        isOpen={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        onAdd={addNewTeam}
      />
      <AdrModal
        isOpen={showAddAdrModal}
        onClose={() => setShowAddAdrModal(false)}
      />
      <ResourceModal
        isOpen={showAddResourceModal}
        onClose={() => setShowAddResourceModal(false)}
      />

      {/* Board-scoped modals */}
      {activeBoardId && (
        <>
          <TaskModal
            isOpen={!!taskModalState?.isOpen}
            onClose={closeTaskModal}
            mode={taskModalState?.mode ?? 'create'}
            taskId={taskModalState?.taskId}
            taskHistory={taskModalState?.taskHistory ?? []}
            historyIndex={taskModalState?.historyIndex ?? -1}
            onNavigate={handleTaskModalNavigate}
            columns={initBoardColumnsStore(activeBoardId).getSnapshot().context.columns.map(c => c.name)}
            onAdd={(task) => {
              const globalTask: GlobalTask = {
                id: task.id,
                title: task.title,
                startDate: task.startDate,
                estimateMode: task.estimateMode,
                estimates: task.estimates,
                assigneeIds: task.assigneeIds,
                categoryIds: task.categoryIds ?? [],
                ...(task.description && { description: task.description }),
                ...(task.consequence && { consequence: task.consequence }),
                ...(task.defaultEstimate && { defaultEstimate: task.defaultEstimate }),
                ...(task.requiredSkills && { requiredSkills: task.requiredSkills }),
              };
              globalTasksStore.send({ type: 'task.add', task: globalTask });
              initBoardTaskViewStore(activeBoardId).send({ type: 'board.addTask', taskId: task.id, column: task.column });
              recalculateSchedule(activeBoardId);
            }}
            onUpdate={(task) => {
              // Update global task fields
              globalTasksStore.send({ type: 'task.updateTitle', taskId: task.id, title: task.title });
              globalTasksStore.send({ type: 'task.updateStartDate', taskId: task.id, startDate: task.startDate });
              globalTasksStore.send({ type: 'task.updateCategories', taskId: task.id, categoryIds: task.categoryIds ?? [] });
              globalTasksStore.send({ type: 'task.updateDescription', taskId: task.id, description: Array.isArray(task.description) ? task.description : undefined });
              globalTasksStore.send({ type: 'task.updateConsequence', taskId: task.id, consequence: Array.isArray(task.consequence) ? task.consequence : undefined });
              globalTasksStore.send({ type: 'task.updateRequiredSkills', taskId: task.id, requiredSkills: typeof task.requiredSkills === 'string' ? task.requiredSkills : undefined });
              globalTasksStore.send({ type: 'task.updateEstimateMode', taskId: task.id, estimateMode: task.estimateMode });
              globalTasksStore.send({ type: 'task.updateEstimates', taskId: task.id, defaultEstimate: task.defaultEstimate, estimates: task.estimates });
              // Update board-specific column
              if (task.column) {
                initBoardTaskViewStore(activeBoardId).send({ type: 'board.moveTaskColumn', taskId: task.id, column: task.column });
              }
              recalculateSchedule(activeBoardId);
            }}
            getTaskById={(tId) => {
              const all = getTasksArray(activeBoardId);
              return all.find(t => t.id === tId);
            }}
            defaultStartDate={activeBoard?.startDate}
            onDelete={(taskId) => {
              globalTasksStore.send({ type: 'task.delete', taskId });
              recalculateSchedule(activeBoardId);
            }}
            onRemoveFromBoard={(taskId) => {
              initBoardTaskViewStore(activeBoardId).send({ type: 'board.removeTask', taskId });
              recalculateSchedule(activeBoardId);
            }}
          />
          <AddColumnModal
            isOpen={showAddColumnModal}
            onClose={() => setShowAddColumnModal(false)}
            onAdd={(name) => {
              initBoardColumnsStore(activeBoardId).send({ type: 'column.add', column: name });
            }}
            existingColumns={initBoardColumnsStore(activeBoardId).getSnapshot().context.columns.map(c => c.name)}
          />
        </>
      )}
    </div>
  );
}
